/** Build year-over-year column series from Annual Performance monthly points. */

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export const YEAR_COLUMN_COLORS = [
  '#1E3A8A', // navy — oldest / cold
  '#2563EB', // blue
  '#0EA5E9', // sky
  '#22D3EE', // cyan
  '#FBBF24', // amber
  '#F97316', // orange
  '#EA580C', // deep orange
  '#DC2626', // red — newest / warm
];

export const CURRENT_YEAR_COLOR = YEAR_COLUMN_COLORS[YEAR_COLUMN_COLORS.length - 1];

export const UNASSIGNED_PROPERTY_NICKNAME = 'Unassigned';
export const PROPERTY_NICKNAME_ORDER = ['2SA', '28LC', '87HP', '67RR'] as const;
export const PROPERTY_COLUMN_COLORS = ['#2563EB', '#EA580C', '#7C3AED', '#0D9488', '#CA8A04', '#DB2777'];

export type PerformancePoint = {
  year: number;
  month: number;
  section: string;
  category: string;
  gross: number;
  signed: number;
  count: number;
};

export type PerformanceSectionMeta = {
  key: string;
  title: string;
};

export type AmountMode = 'gross' | 'signed';
export type ChartMode = 'monthly' | 'cumulative';

export type YearMonthRow = {
  month: string;
  monthNum: number;
  [year: string]: string | number | null;
};

const roundMoney = (n: number): number => Number(n.toFixed(2));

export const amountForPoint = (point: PerformancePoint, mode: AmountMode): number =>
  mode === 'signed' ? Number(point.signed) || 0 : Number(point.gross) || 0;

const hexToRgb = (hex: string): [number, number, number] => {
  const n = hex.replace('#', '');
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
};

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

const colorAlongScale = (t: number, stops: readonly string[]): string => {
  if (stops.length === 0) return '#000000';
  if (stops.length === 1) return stops[0];
  const x = Math.min(1, Math.max(0, t)) * (stops.length - 1);
  const lo = Math.floor(x);
  const hi = Math.min(stops.length - 1, lo + 1);
  const f = x - lo;
  const [r1, g1, b1] = hexToRgb(stops[lo]);
  const [r2, g2, b2] = hexToRgb(stops[hi]);
  return rgbToHex(r1 + (r2 - r1) * f, g1 + (g2 - g1) * f, b1 + (b2 - b1) * f);
};

/** Oldest year is coldest (blue); newest year is warmest (orange/red). */
export const yearColor = (year: number, years: number[], _currentYear?: number): string => {
  const sorted = Array.from(new Set(years)).sort((a, b) => a - b);
  if (sorted.length === 0) return CURRENT_YEAR_COLOR;
  const idx = sorted.indexOf(year);
  const i = idx < 0 ? sorted.length - 1 : idx;
  const t = sorted.length === 1 ? 1 : i / (sorted.length - 1);
  return colorAlongScale(t, YEAR_COLUMN_COLORS);
};

export const filterPointsThroughMonth = (
  points: PerformancePoint[],
  throughMonth: number
): PerformancePoint[] => points.filter((point) => Number(point.month) <= throughMonth);

export const pointsForSection = (
  points: PerformancePoint[],
  sectionKey: string
): PerformancePoint[] => points.filter((point) => point.section === sectionKey);

export const categoriesInSection = (points: PerformancePoint[], sectionKey: string): string[] => {
  const names = new Set<string>();
  pointsForSection(points, sectionKey).forEach((point) => {
    const name = String(point.category || '').trim() || '—';
    names.add(name);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
};

export const sumByYearMonth = (
  points: PerformancePoint[],
  mode: AmountMode
): Map<string, number> => {
  const map = new Map<string, number>();
  points.forEach((point) => {
    const key = `${point.year}-${point.month}`;
    map.set(key, roundMoney((map.get(key) || 0) + amountForPoint(point, mode)));
  });
  return map;
};

export const buildYearMonthRows = (
  points: PerformancePoint[],
  years: number[],
  options: {
    mode: ChartMode;
    amountMode: AmountMode;
    throughMonth: number;
    currentYear: number;
  }
): YearMonthRow[] => {
  const totals = sumByYearMonth(points, options.amountMode);
  const running = new Map<number, number>();
  years.forEach((year) => running.set(year, 0));
  const lastCurrentMonth = Math.min(12, Math.max(1, options.throughMonth));
  const rows: YearMonthRow[] = [];
  for (let month = 1; month <= 12; month += 1) {
    const row: YearMonthRow = {
      month: MONTH_LABELS[month - 1],
      monthNum: month,
    };
    years.forEach((year) => {
      if (year === options.currentYear && month > lastCurrentMonth) {
        row[String(year)] = null;
        return;
      }
      const monthValue = totals.get(`${year}-${month}`) || 0;
      if (options.mode === 'cumulative') {
        const next = roundMoney((running.get(year) || 0) + monthValue);
        running.set(year, next);
        row[String(year)] = next;
      } else {
        row[String(year)] = monthValue;
      }
    });
    rows.push(row);
  }
  return rows;
};

export const hasAnyAmount = (rows: YearMonthRow[], years: number[]): boolean =>
  rows.some((row) => years.some((year) => Number(row[String(year)] || 0) !== 0));

/** Grouped columns omit zero so Recharts does not draw empty stubs. */
export const yearMonthRowsForColumns = (
  rows: YearMonthRow[],
  years: number[]
): YearMonthRow[] =>
  rows.map((row) => {
    const next: YearMonthRow = { month: row.month, monthNum: row.monthNum };
    years.forEach((year) => {
      const value = Number(row[String(year)] || 0);
      next[String(year)] = value === 0 ? null : value;
    });
    return next;
  });

export const STACKED_PL_COLORS = {
  income: '#2563EB',
  expenses: '#EA580C',
  profit: '#16A34A',
};

export const stackedPlKey = (
  year: number,
  field: 'income' | 'expenses' | 'profit'
): string => `${year}_${field}`;

export type StackedPlRow = {
  month: string;
  monthNum: number;
  [key: string]: string | number | null;
};

const yearValue = (row: YearMonthRow | undefined, year: number): number | null => {
  if (!row) return null;
  const value = row[String(year)];
  return value == null ? null : Number(value);
};

const stackedFieldsForYear = (
  year: number,
  incomeVal: number | null,
  expenseVal: number | null,
  profitVal: number | null
): Record<string, number | null> => {
  const k = (field: Parameters<typeof stackedPlKey>[1]) => stackedPlKey(year, field);
  if (incomeVal == null && expenseVal == null && profitVal == null) {
    return {
      [k('income')]: null,
      [k('expenses')]: null,
      [k('profit')]: null,
    };
  }
  const income = Number(incomeVal || 0);
  const expenses = Number(expenseVal || 0);
  const profit = profitVal == null ? roundMoney(income - expenses) : Number(profitVal);
  return {
    [k('income')]: income === 0 ? null : income,
    [k('expenses')]: expenses === 0 ? null : roundMoney(-expenses),
    [k('profit')]: profit === 0 ? null : profit,
  };
};

/** One grouped trio per year: Income above zero, Expenses below zero, Profit signed. */
export const buildStackedPlRows = (
  incomeRows: YearMonthRow[],
  expenseRows: YearMonthRow[],
  netRows: YearMonthRow[],
  years: number[]
): StackedPlRow[] => {
  const n = Math.max(incomeRows.length, expenseRows.length, netRows.length);
  const rows: StackedPlRow[] = [];
  for (let i = 0; i < n; i += 1) {
    const incomeRow = incomeRows[i];
    const month = incomeRow?.month || MONTH_LABELS[i] || String(i + 1);
    const monthNum = incomeRow?.monthNum || i + 1;
    const row: StackedPlRow = { month, monthNum };
    years.forEach((year) => {
      Object.assign(
        row,
        stackedFieldsForYear(
          year,
          yearValue(incomeRow, year),
          yearValue(expenseRows[i], year),
          yearValue(netRows[i], year)
        )
      );
    });
    rows.push(row);
  }
  return rows;
};

export const stackedPlHasAmount = (rows: StackedPlRow[], years: number[]): boolean =>
  rows.some((row) =>
    years.some(
      (year) =>
        Number(row[stackedPlKey(year, 'income')] || 0) !== 0 ||
        Number(row[stackedPlKey(year, 'expenses')] || 0) !== 0
    )
  );

export type YtdTotals = {
  year: number;
  income: number;
  expenses: number;
  net: number;
};

export const ytdTotalsByYear = (
  points: PerformancePoint[],
  years: number[],
  throughMonth: number
): YtdTotals[] => {
  const scoped = filterPointsThroughMonth(points, throughMonth);
  return years.map((year) => {
    let income = 0;
    let expenses = 0;
    scoped
      .filter((point) => point.year === year)
      .forEach((point) => {
        if (point.section === 'income') income += Number(point.gross) || 0;
        if (point.section === 'expenses') expenses += Number(point.gross) || 0;
      });
    return {
      year,
      income: roundMoney(income),
      expenses: roundMoney(expenses),
      net: roundMoney(income - expenses),
    };
  });
};

export const deltaVsPriorYear = (
  current: YtdTotals | undefined,
  prior: YtdTotals | undefined
): { income: number; expenses: number; net: number } => ({
  income: roundMoney((current?.income || 0) - (prior?.income || 0)),
  expenses: roundMoney((current?.expenses || 0) - (prior?.expenses || 0)),
  net: roundMoney((current?.net || 0) - (prior?.net || 0)),
});

export type PropertyIncomePoint = {
  year: number;
  property_id: number;
  nickname: string;
  amount: number;
};

export type PropertyYearRow = {
  year: number;
  [nickname: string]: number | null;
};

export const sortPropertyNicknames = (nicknames: string[]): string[] => {
  const names = nicknames.map((n) => String(n || '').trim()).filter(Boolean);
  const seen = new Set<string>();
  const ordered: string[] = [];
  PROPERTY_NICKNAME_ORDER.forEach((nick) => {
    if (names.includes(nick) && !seen.has(nick)) {
      ordered.push(nick);
      seen.add(nick);
    }
  });
  names
    .filter((n) => !seen.has(n) && n !== UNASSIGNED_PROPERTY_NICKNAME)
    .sort((a, b) => a.localeCompare(b))
    .forEach((n) => {
      ordered.push(n);
      seen.add(n);
    });
  if (names.includes(UNASSIGNED_PROPERTY_NICKNAME)) ordered.push(UNASSIGNED_PROPERTY_NICKNAME);
  return ordered;
};

export const propertyColumnColor = (nickname: string, nicknames: string[]): string => {
  if (nickname === UNASSIGNED_PROPERTY_NICKNAME) return '#94A3B8';
  const named = sortPropertyNicknames(nicknames).filter((n) => n !== UNASSIGNED_PROPERTY_NICKNAME);
  const idx = Math.max(0, named.indexOf(nickname));
  return PROPERTY_COLUMN_COLORS[idx % PROPERTY_COLUMN_COLORS.length];
};

export const buildPropertyIncomeYearRows = (
  points: PropertyIncomePoint[],
  years: number[],
  nicknames: string[]
): PropertyYearRow[] => {
  const totals = new Map<string, number>();
  points.forEach((point) => {
    const key = `${point.year}\0${point.nickname}`;
    totals.set(key, roundMoney((totals.get(key) || 0) + Number(point.amount || 0)));
  });
  const names = sortPropertyNicknames(nicknames);
  return years.map((year) => {
    const row: PropertyYearRow = { year };
    names.forEach((nick) => {
      const value = totals.get(`${year}\0${nick}`) || 0;
      row[nick] = value === 0 ? null : value;
    });
    return row;
  });
};

export const propertyIncomeHasAmount = (rows: PropertyYearRow[], nicknames: string[]): boolean =>
  rows.some((row) => nicknames.some((nick) => Number(row[nick] || 0) !== 0));
